/** Relatórios de gestão: cumprimento de prazos, guias, horas e exportação da lista de tarefas. */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import type { AgruparPor, CumprimentoResposta, GuiasResposta, HorasResposta, PedidosRelatorioResposta } from '@sgc/contracts';
import { get } from '../lib/api';
import { fmtData } from '../lib/formato';
import { COR_STATUS, ROTULO_STATUS, fmtMinutos, fmtMoeda, useDepartamentos } from '../lib/tarefas';
import { Campo, Sel } from '../ui/campos';
import { Kpi, Pill, Sec, Tabs } from '../ui/layout';

const Busca = z.object({ aba: z.enum(['cumprimento', 'guias', 'horas', 'pedidos', 'exportar']).optional(), de: z.string().optional(), ate: z.string().optional() });
export const Route = createFileRoute('/_app/relatorios')({ validateSearch: Busca, component: Relatorios });

const hojeIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const fimDoMes = (iso: string) => new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)), 0).toISOString().slice(0, 10);
const qs = (o: Record<string, string | undefined>) => new URLSearchParams(Object.entries(o).filter(([, v]) => v) as Array<[string, string]>).toString();
const ROTULO_AGRUPAR: Record<AgruparPor, string> = { cliente: 'Cliente', responsavel: 'Responsável', departamento: 'Departamento', obrigacao: 'Obrigação' };

function Relatorios() {
  const s = Route.useSearch();
  const hoje = hojeIso();
  const [aba, setAba] = useState(s.aba ?? 'cumprimento');
  const [de, setDe] = useState(s.de ?? hoje.slice(0, 8) + '01');
  const [ate, setAte] = useState(s.ate ?? fimDoMes(hoje));
  const ok = /^\d{4}-\d{2}-\d{2}$/.test(de) && /^\d{4}-\d{2}-\d{2}$/.test(ate) && de <= ate;
  return (
    <>
      <div className="topbar"><div><h1>Relatórios</h1><div className="sub">gestão do escritório — cada relatório baixa em CSV (abre no Excel)</div></div></div>
      <div className="conteudo">
        <Tabs valor={aba} onChange={setAba} itens={[{ id: 'cumprimento', rotulo: 'Cumprimento de prazos' }, { id: 'guias', rotulo: 'Guias' }, { id: 'horas', rotulo: 'Horas' }, { id: 'pedidos', rotulo: 'Pedidos de clientes' }, { id: 'exportar', rotulo: 'Exportar tarefas' }]} />
        <Sec titulo="Período">
          <div className="fgrid">
            <Campo className="c2" rotulo="De" type="date" valor={de} onChange={setDe} />
            <Campo className="c2" rotulo="Até" type="date" valor={ate} onChange={setAte} />
            <div className="c8 fnote">
              {[-1, 0].map((n) => { const d = new Date(Number(hoje.slice(0, 4)), Number(hoje.slice(5, 7)) - 1 + n, 1); const i = d.toISOString().slice(0, 10); return <button key={n} className="btn link mini" type="button" onClick={() => { setDe(i.slice(0, 8) + '01'); setAte(fimDoMes(i)); }}>{n === 0 ? 'este mês' : 'mês passado'}</button>; })}
              <button className="btn link mini" type="button" onClick={() => { setDe(`${hoje.slice(0, 4)}-01-01`); setAte(`${hoje.slice(0, 4)}-12-31`); }}>este ano</button>
            </div>
          </div>
        </Sec>
        {!ok ? <div className="alerta erro">período inválido</div> : aba === 'cumprimento' ? <Cumprimento de={de} ate={ate} /> : aba === 'guias' ? <Guias de={de} ate={ate} /> : aba === 'horas' ? <Horas de={de} ate={ate} /> : aba === 'pedidos' ? <Pedidos de={de} ate={ate} /> : <Exportar de={de} ate={ate} />}
      </div>
    </>
  );
}

/** `href` aponta para o CSV; o PDF é o mesmo endereço com o formato trocado (abre numa aba, pronto para imprimir ou mandar). */
function Baixar({ href }: { href: string }) {
  return <><a className="btn mini" href={href.replace('formato=csv', 'formato=pdf')} target="_blank" rel="noreferrer">↓ PDF</a><a className="btn mini" href={href}>↓ CSV</a></>;
}

function Cumprimento({ de, ate }: { de: string; ate: string }) {
  const [agrupar, setAgrupar] = useState<AgruparPor>('cliente');
  const [dep, setDep] = useState('');
  const deps = useDepartamentos();
  const q = useQuery({ queryKey: ['rel', 'cumprimento', de, ate, agrupar, dep], queryFn: () => get<CumprimentoResposta>('/v1/relatorios/cumprimento', { de, ate, agruparPor: agrupar, departamento: dep || undefined }), placeholderData: (p) => p });
  const r = q.data;
  const t = r?.totais;
  return (
    <>
      {t && (
        <div className="kpis">
          <Kpi rotulo="Tarefas no período" valor={t.total} sub={`vencendo entre ${fmtData(de)} e ${fmtData(ate)}`} />
          <Kpi rotulo="% no prazo" valor={t.pctNoPrazo ?? '—'} cor={t.pctNoPrazo === null ? undefined : t.pctNoPrazo >= 90 ? 'verde' : t.pctNoPrazo >= 70 ? 'ambar' : 'verm'} sub={`${t.noPrazo} no prazo · ${t.comAtraso} com atraso`} />
          <Kpi rotulo="Atrasadas hoje" valor={t.atrasadasHoje} cor={t.atrasadasHoje ? 'verm' : undefined} sub={`${t.abertas} ainda abertas`} />
          <Kpi rotulo="Média de atraso" valor={t.mediaDiasAtraso === null ? '—' : `${t.mediaDiasAtraso} d`} cor="ambar" sub="nas concluídas com atraso" />
          <Kpi rotulo="Dispensadas" valor={t.dispensadas} />
        </div>
      )}
      <Sec titulo="Por grupo" acoes={<><Sel rotulo="" valor={agrupar} opcoes={Object.entries(ROTULO_AGRUPAR) as Array<[AgruparPor, string]>} onChange={(v) => setAgrupar(v as AgruparPor)} className="c0" />
        <Sel rotulo="" valor={dep} vazio="todos os deptos" opcoes={(deps.data ?? []).map((d) => [d.sigla, d.sigla] as const)} onChange={setDep} className="c0" />
        <Baixar href={`/v1/relatorios/cumprimento?${qs({ de, ate, agruparPor: agrupar, departamento: dep || undefined, formato: 'csv' })}`} /></>}>
        <div className="tbwrap">
          <table className="tb compacta">
            <thead><tr><th>{ROTULO_AGRUPAR[agrupar]}</th><th className="r">Total</th><th className="r">Concluídas</th><th className="r">No prazo</th><th className="r">Com atraso</th><th className="r">Dispensadas</th><th className="r">Abertas</th><th className="r">Atrasadas hoje</th><th className="r">% no prazo</th><th className="r">Média atraso</th></tr></thead>
            <tbody>
              {r?.grupos.length === 0 && <tr><td className="vazio" colSpan={10}>nenhuma tarefa no período</td></tr>}
              {r?.grupos.map((g) => (
                <tr key={g.chave}>
                  <td>{agrupar === 'cliente' && g.chave !== '—' ? <Link to="/tarefas" search={{ aba: 'lista', de, ate, busca: g.nome, status: undefined }}>{g.nome}</Link> : g.nome}</td>
                  <td className="r">{g.total}</td><td className="r">{g.concluidas}</td><td className="r" style={{ color: 'var(--green)' }}>{g.noPrazo}</td><td className="r" style={{ color: g.comAtraso ? 'var(--amber)' : undefined }}>{g.comAtraso}</td><td className="r dim">{g.dispensadas}</td><td className="r">{g.abertas}</td>
                  <td className="r">{g.atrasadasHoje ? <Pill cor="verm">{g.atrasadasHoje}</Pill> : <span className="dim">0</span>}</td>
                  <td className="r"><b>{g.pctNoPrazo === null ? '—' : `${g.pctNoPrazo}%`}</b></td><td className="r dim">{g.mediaDiasAtraso === null ? '—' : `${g.mediaDiasAtraso} d`}</td>
                </tr>
              ))}
              {t && t.total > 0 && <tr className="tot"><td><b>Total</b></td><td className="r"><b>{t.total}</b></td><td className="r"><b>{t.concluidas}</b></td><td className="r"><b>{t.noPrazo}</b></td><td className="r"><b>{t.comAtraso}</b></td><td className="r"><b>{t.dispensadas}</b></td><td className="r"><b>{t.abertas}</b></td><td className="r"><b>{t.atrasadasHoje}</b></td><td className="r"><b>{t.pctNoPrazo === null ? '—' : `${t.pctNoPrazo}%`}</b></td><td className="r"><b>{t.mediaDiasAtraso ?? '—'}</b></td></tr>}
            </tbody>
          </table>
        </div>
        <div className="fnote">% no prazo = concluídas no prazo ÷ (concluídas + atrasadas em aberto). Dispensadas não entram no cálculo.</div>
      </Sec>
    </>
  );
}

function Guias({ de, ate }: { de: string; ate: string }) {
  const [sit, setSit] = useState<'todas' | 'abertas' | 'pagas' | 'vencidas'>('todas');
  const q = useQuery({ queryKey: ['rel', 'guias', de, ate, sit], queryFn: () => get<GuiasResposta>('/v1/relatorios/guias', { de, ate, situacao: sit }), placeholderData: (p) => p });
  const r = q.data;
  const t = r?.totais;
  return (
    <>
      {t && (
        <div className="kpis">
          <Kpi rotulo="Guias no período" valor={t.quantidade} sub={fmtMoeda(t.valorTotal)} />
          <Kpi rotulo="Em aberto" valor={t.abertas} cor="ambar" sub={fmtMoeda(t.valorAberto)} />
          <Kpi rotulo="Vencidas sem pagamento" valor={t.vencidas} cor={t.vencidas ? 'verm' : undefined} sub={fmtMoeda(t.valorVencido)} />
          <Kpi rotulo="Pagas" valor={t.pagas} cor="verde" sub={fmtMoeda(t.valorPago)} />
        </div>
      )}
      <Sec titulo="Guias" sub="registradas na aba Entrega das tarefas, pelo vencimento da guia" acoes={<>
        <div className="tabs">{(['todas', 'abertas', 'vencidas', 'pagas'] as const).map((x) => <button key={x} type="button" className={sit === x ? 'active' : undefined} onClick={() => setSit(x)}>{x}</button>)}</div>
        <Baixar href={`/v1/relatorios/guias?${qs({ de, ate, situacao: sit, formato: 'csv' })}`} /></>}>
        <div className="tbwrap">
          <table className="tb compacta">
            <thead><tr><th>Vencimento</th><th>Cliente</th><th>Obrigação</th><th>Comp.</th><th>Código</th><th className="r">Valor</th><th>Pago em</th><th>Atraso</th><th>Resp.</th><th>Tarefa</th></tr></thead>
            <tbody>
              {r?.itens.length === 0 && <tr><td className="vazio" colSpan={10}>nenhuma guia</td></tr>}
              {r?.itens.map((g) => (
                <tr key={g.entregaId}>
                  <td className="mono"><Link to="/tarefas" search={{ aba: 'lista', t: g.tarefaId }}>{fmtData(g.vencimentoGuia)}</Link></td>
                  <td>{g.clienteNome ?? <span className="dim">—</span>}</td><td><b>{g.obrigacaoSigla ?? g.titulo}</b></td><td className="mono">{g.competenciaLabel}</td><td className="mono dim">{g.codigoReceita}</td>
                  <td className="r">{fmtMoeda(g.valorTotal)}</td>
                  <td className="mono">{g.dataPagamento ? fmtData(g.dataPagamento) : <Pill cor={g.diasAtrasoPagamento ? 'verm' : 'ambar'}>{g.diasAtrasoPagamento ? 'vencida' : 'em aberto'}</Pill>}</td>
                  <td className="r">{g.diasAtrasoPagamento ? <span style={{ color: 'var(--red)' }}>{g.diasAtrasoPagamento} d</span> : <span className="dim">—</span>}</td>
                  <td className="mono">{g.responsavelApelido ?? '—'}</td><td><Pill cor={COR_STATUS[g.statusTarefa]}>{ROTULO_STATUS[g.statusTarefa]}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sec>
    </>
  );
}

function Horas({ de, ate }: { de: string; ate: string }) {
  const [agrupar, setAgrupar] = useState<'membro' | 'cliente' | 'obrigacao' | 'departamento'>('membro');
  const q = useQuery({ queryKey: ['rel', 'horas', de, ate, agrupar], queryFn: () => get<HorasResposta>('/v1/relatorios/horas', { de, ate, agruparPor: agrupar }), placeholderData: (p) => p });
  const r = q.data;
  return (
    <Sec titulo="Horas apontadas" sub={r ? `${fmtMinutos(r.totalMinutos)} no período` : undefined} acoes={<>
      <Sel rotulo="" valor={agrupar} opcoes={[['membro', 'Por pessoa'], ['cliente', 'Por cliente'], ['obrigacao', 'Por obrigação'], ['departamento', 'Por departamento']]} onChange={(v) => setAgrupar(v as typeof agrupar)} className="c0" />
      <Baixar href={`/v1/relatorios/horas?${qs({ de, ate, agruparPor: agrupar, formato: 'csv' })}`} /></>}>
      <div className="tbwrap">
        <table className="tb compacta">
          <thead><tr><th>Grupo</th><th className="r">Horas</th><th className="r">Apontamentos</th><th className="r">Tarefas</th><th className="r">%</th><th style={{ width: 160 }} /></tr></thead>
          <tbody>
            {r?.grupos.length === 0 && <tr><td className="vazio" colSpan={6}>nenhuma hora apontada no período — use o cronômetro ou o lançamento manual na aba Horas da tarefa</td></tr>}
            {r?.grupos.map((g) => (
              <tr key={g.chave}><td>{g.nome}</td><td className="r"><b>{fmtMinutos(g.minutos)}</b></td><td className="r">{g.apontamentos}</td><td className="r">{g.tarefas}</td><td className="r">{g.pct}%</td><td><div className="barra"><span style={{ width: `${g.pct}%`, background: 'var(--accent)' }} /></div></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </Sec>
  );
}

function Pedidos({ de, ate }: { de: string; ate: string }) {
  const q = useQuery({ queryKey: ['rel', 'pedidos', de, ate], queryFn: () => get<PedidosRelatorioResposta>('/v1/relatorios/pedidos', { de, ate }), placeholderData: (p) => p });
  const r = q.data;
  const t = r?.totais;
  const tabela = (titulo: string, grupos: PedidosRelatorioResposta['porServico']) => (
    <Sec titulo={titulo}>
      <div className="tbwrap"><table className="tb compacta">
        <thead><tr><th>Grupo</th><th className="r">Pedidos</th><th className="r">Abertos</th><th className="r">Atrasados</th><th className="r">Concluídos</th><th className="r">No prazo</th><th className="r">Recusados</th><th className="r">Tempo médio</th></tr></thead>
        <tbody>
          {grupos.length === 0 && <tr><td className="vazio" colSpan={8}>nenhum pedido no período</td></tr>}
          {grupos.map((g) => <tr key={g.chave}><td><b>{g.nome}</b></td><td className="r">{g.pedidos}</td><td className="r">{g.abertos}</td><td className="r">{g.atrasados ? <span style={{ color: 'var(--red)' }}>{g.atrasados}</span> : 0}</td><td className="r">{g.concluidos}</td><td className="r">{g.concluidos ? `${Math.round((g.noPrazo / g.concluidos) * 100)}%` : '—'}</td><td className="r">{g.recusados}</td><td className="r">{g.mediaDias === null ? '—' : `${g.mediaDias} d`}</td></tr>)}
        </tbody>
      </table></div>
    </Sec>
  );
  return (
    <>
      {t && <div className="kpis">
        <Kpi rotulo="Pedidos no período" valor={t.pedidos} sub="pela data do pedido" />
        <Kpi rotulo="Em atendimento" valor={t.abertos} cor="azul" sub={t.atrasados ? `${t.atrasados} atrasado(s)` : undefined} />
        <Kpi rotulo="Concluídos" valor={t.concluidos} cor="verde" sub={t.concluidos ? `${Math.round((t.noPrazo / t.concluidos) * 100)}% no prazo` : undefined} />
        <Kpi rotulo="Tempo médio" valor={t.mediaDias === null ? '—' : `${t.mediaDias} d`} sub="do pedido à conclusão" />
      </div>}
      {tabela('Por serviço', r?.porServico ?? [])}
      {tabela('Por departamento', r?.porDepartamento ?? [])}
    </>
  );
}

function Exportar({ de, ate }: { de: string; ate: string }) {
  const [status, setStatus] = useState('');
  const [dep, setDep] = useState('');
  const deps = useDepartamentos();
  const href = `/v1/relatorios/tarefas.csv?${qs({ de, ate, status: status || undefined, departamento: dep || undefined, somenteAbertas: status ? undefined : 'false' })}`;
  return (
    <Sec titulo="Exportar tarefas" sub="a lista completa do período, com os filtros escolhidos (até 5.000 linhas)">
      <div className="fgrid">
        <Sel className="c3" rotulo="Status" valor={status} vazio="todos" opcoes={Object.entries(ROTULO_STATUS).filter(([k]) => k !== 'CANCELADA')} onChange={setStatus} />
        <Sel className="c3" rotulo="Departamento" valor={dep} vazio="todos" opcoes={(deps.data ?? []).map((d) => [d.sigla, `${d.sigla} · ${d.nome}`] as const)} onChange={setDep} />
        <div className="c3 fnote"><a className="btn primario" href={href}>↓ Baixar CSV</a></div>
      </div>
      <div className="fnote">Colunas: vencimento, cliente, CNPJ/CPF, obrigação, título, competência, departamento, status, responsável, prazo interno, concluída em, dias de atraso.</div>
    </Sec>
  );
}
